// ---------------------------------------------------------------------------
// Theme preferences — the *type* + helpers Tailwind code reads from. The MUI
// theme this file used to build is gone; shadcn/Tailwind read colours from
// CSS variables defined in `src/index.css`, so all this file owns now is the
// preference shape, default values, and the dyslexia-mode predicate.
// ---------------------------------------------------------------------------

export interface ThemePreferences {
  mode: 'dark' | 'light' | 'system';
  highContrast: boolean;
  fontFamily: 'default' | 'dyslexia' | 'dyslexia-hc';
  fontSize: 'small' | 'medium' | 'large' | 'xl';
  reducedMotion: boolean;
  // Reading mode (dyslexia-friendly UI) — distinct from the
  // fontFamily toggle. Toggles `html.reading` globally for
  // Atkinson Hyperlegible + warm cream surfaces + suppressed
  // serif headings. Persisted server-side via
  // PlanningPreference.ReadingModeEnabled so it follows the user
  // across machines; localStorage holds the same value for the
  // fast-paint bootstrap before the server response lands.
  readingMode: boolean;
  // Living background: a faint time-of-day tint on the page behind the
  // workspace, dark mode only. On by default; off leaves a flat page.
  livingBackground: boolean;
}

export const defaultPreferences: ThemePreferences = {
  // Workspace defaults to following the OS appearance. The redesign
  // uses light for the workspace and dark for the cinematic moments
  // (Login, Brief, focus session); 'system' lets the user's OS pick.
  mode: 'system',
  highContrast: false,
  fontFamily: 'default',
  fontSize: 'medium',
  reducedMotion: false,
  readingMode: false,
  livingBackground: true,
};

/** High contrast is on: its own switch, or the high-contrast dyslexia font, which promises it too. */
export function isHighContrast(prefs: Pick<ThemePreferences, 'highContrast' | 'fontFamily'>): boolean {
  return prefs.highContrast || prefs.fontFamily === 'dyslexia-hc';
}

export function isDyslexiaTheme(fontFamily: ThemePreferences['fontFamily']): boolean {
  return fontFamily === 'dyslexia' || fontFamily === 'dyslexia-hc';
}
